import { Skeleton } from "components/DesignSystem/Skeleton/Skeleton";
import { CalendarBlank } from "components/icons/CalendarBlank";
import { FileEtd } from "components/icons/FileEtd";
import { RightArrow } from "components/icons/RightArrow";
import { useCurrentGroupContext } from "hooks/useCurrentGroupContext";
import {
  useGetFilingsByStatusQuery,
  type FilingByStatus,
} from "store/apis/task";
import { ConditionalLink } from "components/conditionalLink";
import { FILING_STATUS } from "constants/filingStatus";
import { formatDate } from "utils/formatDate";
import { getClientStageLabel } from "utils/clientProgress";
import { type TaskStepDetails } from "types/Models/task";
import { JourneyTooltip } from "components/home/TaxComponents/JourneyTooltip";
import { CardFadeOverlay } from "./CardFadeOverlay";
import { TasksCardEmptyState } from "./TasksCardEmptyState";
import ToolTip from "components/design/toolTip";

const EMPTY_FILINGS: FilingByStatus[] = [];

const toStepDetails = (filing: FilingByStatus): TaskStepDetails[] =>
  (filing.client_progress?.stages ?? []).map((stage) => ({
    name: getClientStageLabel({ key: stage.key, label: stage.label }),
    key: stage.key,
    isCompleted: stage.is_completed,
    isCurrent: stage.is_current,
    isIncomplete: !stage.is_completed && !stage.is_current,
    description: stage.description ?? undefined,
    isOnHold: stage.is_on_hold,
  }));

type SeasonGroup = {
  season: string;
  filings: FilingByStatus[];
};

// Group filings by season, earliest season first (per design annotation).
const groupFilingsBySeason = (filings: FilingByStatus[]): SeasonGroup[] => {
  const bySeason = new Map<string, FilingByStatus[]>();

  filings.forEach((filing) => {
    const existing = bySeason.get(filing.season) ?? [];
    existing.push(filing);
    bySeason.set(filing.season, existing);
  });

  return Array.from(bySeason.entries())
    .sort(([seasonA], [seasonB]) => Number(seasonA) - Number(seasonB))
    .map(([season, seasonFilings]) => ({ season, filings: seasonFilings }));
};

const SeasonHeader = ({ season }: { season: string }) => (
  <div className="t-border-0 t-border-b t-border-solid t-border-neutral-10 t-bg-surface-lighter-grey t-px-5 t-py-1.5">
    <div className="t-text-caption t-uppercase t-text-text-60">
      Season {season}
    </div>
  </div>
);

const InProgressRow = ({ filing }: { filing: FilingByStatus }) => {
  const stepDetails = toStepDetails(filing);

  return (
    <ConditionalLink
      to={`/tax/filings/${filing.filing_id}`}
      className="t-group t-flex t-items-center t-justify-between t-gap-3 t-border-0 t-border-b t-border-solid t-border-neutral-10 t-p-5 last:t-border-b-0 hover:t-bg-surface-lighter-grey"
    >
      <div className="t-flex t-flex-col t-gap-1.5">
        <div className="t-text-caption t-uppercase t-text-neutral-40">
          {filing.entity_name}
        </div>
        <div className="t-text-body t-text-text-60">{filing.filing_name}</div>
        <div className="t-flex t-items-center t-gap-3">
          <JourneyTooltip
            stepDetails={stepDetails}
            latestEtd={filing.latest_etd ?? null}
            taskUuid={filing.filing_id}
            previousCycles={filing.client_progress?.previous_cycles}
          >
            <span className="t-text-body-sm t-text-text-30">
              {filing.status}
            </span>
          </JourneyTooltip>
          <span className="t-h-2.5 t-w-px t-bg-neutral-10" />
          <div className="t-flex t-items-center t-gap-1 t-text-text-30">
            <CalendarBlank />
            <span className="t-text-body-sm">
              {formatDate(filing.deadline)}
            </span>
          </div>
          {filing.latest_etd && (
            <>
              <span className="t-h-2.5 t-w-px t-bg-neutral-10" />
              <ToolTip
                text={`Your preparer expects to share a draft of the filing by ${formatDate(
                  filing.latest_etd
                )} for your review.`}
                propClass="t-w-72 !t-text-left"
              >
                <div className="t-flex t-items-center t-gap-1 t-text-text-30">
                  <FileEtd className="t-shrink-0" />
                  <span className="t-text-body-sm">
                    {formatDate(filing.latest_etd)}
                  </span>
                </div>
              </ToolTip>
            </>
          )}
        </div>
      </div>
      <div className="t-hidden t-shrink-0 t-text-text-60 group-hover:t-block">
        <RightArrow />
      </div>
    </ConditionalLink>
  );
};

const InProgressSkeleton = () => (
  <div className="t-flex t-flex-col t-gap-6 t-p-6">
    {[0, 1, 2].map((row) => (
      <div key={row} className="t-flex t-flex-col t-gap-2">
        <Skeleton className="t-h-4 t-w-1/3" />
        <Skeleton className="t-h-5 t-w-2/3" />
        <Skeleton className="t-h-4 t-w-1/2" />
      </div>
    ))}
  </div>
);

type InProgressCardProps = {
  entityIds: string[];
  isScopeReady: boolean;
};

export const InProgressCard = ({
  entityIds,
  isScopeReady,
}: InProgressCardProps) => {
  const { uuid: groupId } = useCurrentGroupContext();
  const {
    data: allFilings = EMPTY_FILINGS,
    isLoading,
    isError,
  } = useGetFilingsByStatusQuery(
    { groupId, entityIds },
    { skip: !groupId || !isScopeReady, refetchOnMountOrArgChange: true }
  );

  const filings = allFilings.filter(
    (filing) => filing.status !== FILING_STATUS.FILED
  );
  const seasonGroups = groupFilingsBySeason(filings);

  let content = (
    <>
      <div className="hide-scrollbar t-h-full t-overflow-y-auto t-pb-6">
        {seasonGroups.map((group) => (
          <div key={group.season}>
            <SeasonHeader season={group.season} />
            {group.filings.map((filing) => (
              <InProgressRow key={filing.filing_id} filing={filing} />
            ))}
          </div>
        ))}
      </div>
      <CardFadeOverlay />
    </>
  );

  if (isLoading) {
    content = <InProgressSkeleton />;
  }

  if (isError) {
    content = (
      <TasksCardEmptyState
        title="Couldn't load filings"
        description="Something went wrong while loading your filings. Please try again."
      />
    );
  }

  if (!isLoading && !isError && filings.length === 0) {
    content = (
      <TasksCardEmptyState
        title="Nothing in progress"
        description="Once a filing starts, you'll track every step of it right here."
      />
    );
  }

  return (
    <div className="t-flex t-flex-col t-gap-4">
      <div className="t-flex t-h-8 t-items-center">
        <div className="t-whitespace-nowrap t-text-subtext-lg t-text-text-60">
          Pending on Inkle
        </div>
      </div>
      <div className="t-relative t-h-[400px] t-overflow-hidden t-rounded-2xl t-border t-border-solid t-border-neutral-10 t-bg-white">
        {content}
      </div>
    </div>
  );
};
